import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { ACCOUNT_PATHS } from './paths';
import { HOME, takeReturnTo } from './returnTo';
import { PrimaryLink, RouterLink } from './authUi';
import { useMe } from './meContext';

/**
 * Where WorkOS sends people back to (`redirectUri`).
 *
 * AuthKit swaps `?code=` for a session only once its client is up, so nothing
 * here moves until it has: leave any sooner and the code goes with the page it
 * arrived on. Then it asks the API whether this account has answered the
 * sign-up questions: a new account goes to them first, carrying where it was
 * headed, and everyone else goes straight there.
 */
export default function CallbackPage() {
  const { status } = useMe();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    if (status.kind !== 'ready' && status.kind !== 'error') return;
    done.current = true;
    // AuthKit hands the destination over once.
    const to = takeReturnTo() ?? HOME;
    if (status.kind === 'ready' && !status.me.onboardingComplete) {
      navigate(ACCOUNT_PATHS.welcome, { replace: true, state: { returnTo: to } });
      return;
    }
    // Onboarding done, or the API unreachable: the site works either way.
    navigate(to.path, { replace: true, state: to.arrival ?? null });
  }, [status, navigate]);

  if (status.kind === 'logged-out') {
    return (
      <Box sx={{ textAlign: 'center', pt: 4 }}>
        <Typography
          component="h1"
          sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 24, mb: 1, color: BRAND.parchment }}
        >
          We couldn&rsquo;t log you in
        </Typography>
        <Typography sx={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(243,238,231,0.78)', mb: 3 }}>
          The link may have expired, or login started in another browser. Please try again.
        </Typography>
        <PrimaryLink to={ACCOUNT_PATHS.logIn} replace>
          Try again
        </PrimaryLink>
        <Box sx={{ mt: 2.5, fontSize: 14 }}>
          <RouterLink to="/">Back to My Climate Week</RouterLink>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', gap: 1.5, pt: 6 }} role="status">
      <CircularProgress sx={{ color: BRAND.parchment }} />
      <Typography sx={{ fontSize: 15, color: BRAND.parchment }}>Logging you in…</Typography>
    </Box>
  );
}
